// src/lib/snapshotService.ts
//
// BekalNanti — Milestone P10: Plan Snapshot & Re-Map Authority
// Provides snapshot persistence and re-mapping comparison.
// Safe, durable, zero data loss, zero destructive overwrites.

import { GoalRecord } from '../journey/types'
import { PetaEsokTrajectoryMap } from './petaEsokTypes'
import { PetaEsokActionItem } from './actionTypes'
import { PlanSnapshotRecord, LifeEventType, ReMapComparisonResult } from './snapshotTypes'

const SNAPSHOT_STORAGE_PREFIX = 'bekalnanti_snapshots_'

/**
 * Creates a frozen plan snapshot capturing goal, trajectory, and action state.
 */
export function createPlanSnapshot(
  goal: GoalRecord,
  trajectory: PetaEsokTrajectoryMap,
  actions: PetaEsokActionItem[],
  triggerEvent: LifeEventType = 'manual_checkpoint',
  triggerNote: string = 'Titik simpan perencanaan'
): PlanSnapshotRecord {
  const existingSnapshots = getPlanSnapshots(goal.id)
  const nextVersion = existingSnapshots.length + 1

  const snapshot: PlanSnapshotRecord = {
    id: `snap-${goal.id}-${Date.now()}`,
    goalId: goal.id,
    householdId: goal.householdId || null,
    createdAt: new Date().toISOString(),
    triggerEvent,
    triggerNote,
    snapshotVersion: nextVersion,
    goalSnapshot: JSON.parse(JSON.stringify(goal)),
    trajectorySnapshot: JSON.parse(JSON.stringify(trajectory)),
    actionsSnapshot: JSON.parse(JSON.stringify(actions)),
    summary: {
      totalFutureCost: trajectory.bekal.totalProjectedNeed,
      requiredMonthlySaving: trajectory.bekal.requiredMonthlySavingConfirmed,
      realityCheckStatus: trajectory.realityCheckStatus,
      confirmedFunding: trajectory.bekal.confirmedFundingTotal,
      targetYear: trajectory.targetHorizonYear,
    },
  }

  savePlanSnapshot(snapshot)
  return snapshot
}

/**
 * Retrieves all stored snapshots for a specific goal.
 */
export function getPlanSnapshots(goalId: string): PlanSnapshotRecord[] {
  try {
    const raw = localStorage.getItem(`${SNAPSHOT_STORAGE_PREFIX}${goalId}`)
    if (!raw) return []
    return JSON.parse(raw) as PlanSnapshotRecord[]
  } catch {
    return []
  }
}

/**
 * Persists a snapshot into local durable storage.
 */
export function savePlanSnapshot(snapshot: PlanSnapshotRecord): void {
  try {
    const existing = getPlanSnapshots(snapshot.goalId)
    const updated = [snapshot, ...existing]
    localStorage.setItem(`${SNAPSHOT_STORAGE_PREFIX}${snapshot.goalId}`, JSON.stringify(updated))
  } catch (err) {
    console.error('Failed to save plan snapshot:', err)
  }
}

/**
 * Compares current live trajectory against a previous historical snapshot (Re-Map Comparison).
 */
export function compareReMapTrajectory(
  previousSnapshot: PlanSnapshotRecord,
  currentTrajectory: PetaEsokTrajectoryMap
): ReMapComparisonResult {
  const prevSummary = previousSnapshot.summary
  const curCost = currentTrajectory.bekal.totalProjectedNeed
  const curSaving = currentTrajectory.bekal.requiredMonthlySavingConfirmed
  const curStatus = currentTrajectory.realityCheckStatus
  const curYear = currentTrajectory.targetHorizonYear

  const costDiff = curCost - prevSummary.totalFutureCost
  const savingDiff = curSaving - prevSummary.requiredMonthlySaving
  const statusChanged = curStatus !== prevSummary.realityCheckStatus
  const targetYearChanged = curYear !== prevSummary.targetYear

  let explanation = 'Perencanaan telah diperbarui.'
  if (savingDiff < 0) {
    explanation = `Kebutuhan tabungan bulanan berkurang Rp ${Math.abs(savingDiff).toLocaleString('id-ID')}/bln dibanding versi ${previousSnapshot.snapshotVersion}.`
  } else if (savingDiff > 0) {
    explanation = `Kebutuhan tabungan bulanan meningkat Rp ${savingDiff.toLocaleString('id-ID')}/bln dibanding versi ${previousSnapshot.snapshotVersion}.`
  }

  return {
    previousSnapshot,
    currentTrajectory,
    delta: {
      costDiff,
      savingDiff,
      statusChanged,
      targetYearChanged,
      explanation,
    },
  }
}
